"use client"

import * as React from 'react'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type PositionRow = {
  id: string
  locomotiveId: string
  adapterId: string
  latitude: number
  longitude: number
  speedKmh: number | null
  headingDegrees: number | null
  nearestStationId: string | null
  recordedAt: string
}

type PositionsResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

function mapPositionRow(item: Record<string, unknown>): PositionRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    locomotiveId: typeof item.locomotive_id === 'string' ? item.locomotive_id : '',
    adapterId: typeof item.adapter_id === 'string' ? item.adapter_id : '',
    latitude: typeof item.latitude === 'number' ? item.latitude : Number(item.latitude ?? 0),
    longitude: typeof item.longitude === 'number' ? item.longitude : Number(item.longitude ?? 0),
    speedKmh: item.speed_kmh != null ? Number(item.speed_kmh) : null,
    headingDegrees: item.heading_degrees != null ? Number(item.heading_degrees) : null,
    nearestStationId: typeof item.nearest_station_id === 'string' ? item.nearest_station_id : null,
    recordedAt: typeof item.recorded_at === 'string' ? item.recorded_at : '',
  }
}

export default function PositionHistoryPage() {
  const [rows, setRows] = React.useState<PositionRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    return params.toString()
  }, [page])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: PositionsResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<PositionsResponse>(
          `/api/rail_tracking/positions?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('rail_tracking.positions.error.load', 'Failed to load positions')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapPositionRow).filter((row): row is PositionRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('rail_tracking.positions.error.load', 'Failed to load positions')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  const columns = React.useMemo<ColumnDef<PositionRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'locomotiveId',
        header: t('rail_tracking.positions.columns.locomotiveId', 'Locomotive'),
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.locomotiveId.slice(0, 8)}…</span>
        ),
      },
      {
        accessorKey: 'latitude',
        header: t('rail_tracking.positions.columns.latitude', 'Lat'),
        cell: ({ row }) => row.original.latitude.toFixed(6),
      },
      {
        accessorKey: 'longitude',
        header: t('rail_tracking.positions.columns.longitude', 'Lon'),
        cell: ({ row }) => row.original.longitude.toFixed(6),
      },
      {
        accessorKey: 'speedKmh',
        header: t('rail_tracking.positions.columns.speedKmh', 'Speed'),
        cell: ({ row }) => row.original.speedKmh !== null ? `${row.original.speedKmh} km/h` : noValue,
      },
      {
        accessorKey: 'headingDegrees',
        header: t('rail_tracking.positions.columns.heading', 'Heading'),
        cell: ({ row }) => row.original.headingDegrees !== null ? `${row.original.headingDegrees}°` : noValue,
      },
      {
        accessorKey: 'recordedAt',
        header: t('rail_tracking.positions.columns.recordedAt', 'Recorded At'),
        cell: ({ row }) => row.original.recordedAt
          ? new Date(row.original.recordedAt).toLocaleString()
          : noValue,
      },
    ]
  }, [t])

  return (
    <Page>
      <PageBody>
        <DataTable<PositionRow>
          title={t('rail_tracking.positions.title', 'Position History')}
          refreshButton={{
            label: t('rail_tracking.positions.actions.refresh', 'Refresh'),
            onRefresh: () => { setPage(1); handleRefresh() },
          }}
          columns={columns}
          data={rows}
          perspective={{ tableId: 'rail_tracking.positions.list' }}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
    </Page>
  )
}
